import { useEffect, useState } from "react";
import { Icon } from "../../components/ui/Icon";
import { Orb } from "../../components/ui/Orb";
import type { RunEvent, RunInfo } from "../../lib/productsApi";
import { runSteps } from "./runSteps";
import { mmss, parseTime, plural } from "./text";

const SHOWN = 6;

/** Seconds since a time, ticking once a second while `on`. */
function useElapsed(since: string | null, on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [on]);
  return since ? Math.max(0, (now - parseTime(since).getTime()) / 1000) : 0;
}

/** What a live run shows: its newest steps, how many came before, and a line on how long it has run. */
function useLiveRun(run: RunInfo | null, events: RunEvent[]) {
  const steps = runSteps(events);
  const reading = steps.some((s) => s.name === "read_page");
  const secs = useElapsed(run?.started_at ?? null, run?.state === "running");
  const shown = steps.slice(-SHOWN);
  const earlier = steps.length - shown.length;

  let fine = "Starting. You can leave this page; it keeps going.";
  if (run?.state === "running") fine = `Running for ${mmss(secs)}. You can leave this page; it keeps going.`;
  if (run?.state === "interrupted") fine = "It stopped for a moment and picks up where it was.";
  return { steps, reading, shown, earlier, fine };
}

/** A learn run working, live (the prototype's "Learning the business" card): its newest steps as they happen. */
export function LearnLive({ run, events }: { run: RunInfo | null; events: RunEvent[] }) {
  const { steps, reading, shown, earlier, fine } = useLiveRun(run, events);

  return (
    <section className="card">
      <div className="busy">
        <div className="orb-tile">
          <Orb />
        </div>
        <h3>{reading ? "Reading its pages" : "Looking for the business"}</h3>
        <p className="fine">{fine}</p>
        {steps.length > 0 && (
          <div className="dsteps">
            {earlier > 0 && <p className="fine dsteps-more">{plural(earlier, "earlier step")}</p>}
            {shown.map((s) => (
              <div className="dstep" key={s.id}>
                {s.result === null ? <span className="spin" /> : <Icon name={s.failed ? "x" : s.result === "waiting for you" ? "check" : "search"} />}
                <span>
                  <b>{s.title}</b>
                  <br />
                  <span className="dstep-sub">{s.result ?? "Working on it"}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/** The same live run on a phone or an iPad: what it is doing now, then its newest steps as rows. */
export function LearnLiveNative({ run, events }: { run: RunInfo | null; events: RunEvent[] }) {
  const { steps, reading, shown, earlier, fine } = useLiveRun(run, events);
  return (
    <>
      <div className="pn-card pd-live">
        <span className="pd-spin" aria-hidden="true" />
        <div>
          <h3>{reading ? "Reading its pages" : "Looking for the business"}</h3>
          <p>{fine}</p>
        </div>
      </div>
      {steps.length > 0 && (
        <section>
          <div className="pn-cap">What it did, newest last</div>
          <div className="pn-grp">
            {shown.map((s) => (
              <div className="pn-row static wrap pd-runstep" key={s.id}>
                <span className={s.failed ? "pn-ico danger" : s.result === null ? "pn-ico" : "pn-ico plain"}>
                  {s.result === null ? <span className="pd-spin" /> : <Icon name={s.failed ? "x" : s.result === "waiting for you" ? "check" : "search"} />}
                </span>
                <span className="m">
                  <span className="t">{s.title}</span>
                  <span className="s">{s.result ?? "Working on it"}</span>
                </span>
              </div>
            ))}
          </div>
          {earlier > 0 && <p className="pn-note">{plural(earlier, "earlier step")}</p>}
        </section>
      )}
    </>
  );
}
